import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Brush, CartesianGrid, ReferenceLine
} from 'recharts';
import { Heart, Mountain, Gauge, Footprints, Zap, Thermometer, TrendingDown, ChevronsUpDown } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import api from '../utils/axiosConfig';

const CHART_CONFIG = {
  altitude: {
    key: 'altitude',
    label: 'Elevation',
    unit: 'm',
    color: '#6366f1',
    gradientId: 'altGradient',
    icon: Mountain,
    type: 'area',
    decimals: 0,
  },
  velocity_smooth: {
    key: 'pace',
    label: 'Pace',
    unit: '/km',
    color: '#22c55e',
    icon: Gauge,
    type: 'line',
    reversed: true,
    formatValue: (v) => {
      if (!v || v <= 0) return '-';
      const totalSec = Math.round(v);
      const m = Math.floor(totalSec / 60);
      const s = totalSec % 60;
      return `${m}:${s.toString().padStart(2, '0')}`;
    },
    formatTick: (v) => {
      if (!v || v <= 0) return '';
      const totalSec = Math.round(v);
      const m = Math.floor(totalSec / 60);
      const s = totalSec % 60;
      return `${m}:${s.toString().padStart(2, '0')}`;
    },
  },
  heartrate: {
    key: 'heartrate',
    label: 'Heart Rate',
    unit: 'bpm',
    color: '#ef4444',
    icon: Heart,
    type: 'line',
    decimals: 0,
  },
  cadence: {
    key: 'cadence',
    label: 'Cadence',
    unit: 'spm',
    color: '#f59e0b',
    icon: Footprints,
    type: 'line',
    decimals: 0,
  },
  watts: {
    key: 'watts',
    label: 'Power',
    unit: 'W',
    color: '#a855f7',
    icon: Zap,
    type: 'line',
    decimals: 0,
  },
  grade_smooth: {
    key: 'grade',
    label: 'Grade',
    unit: '%',
    color: '#64748b',
    icon: TrendingDown,
    type: 'line',
    decimals: 1,
  },
  temp: {
    key: 'temp',
    label: 'Temperature',
    unit: '°C',
    color: '#06b6d4',
    icon: Thermometer,
    type: 'line',
    decimals: 0,
  },
};

const STREAM_ORDER = ['altitude', 'velocity_smooth', 'heartrate', 'cadence', 'watts', 'grade_smooth', 'temp'];

function formatDurationSec(sec) {
  if (sec == null || isNaN(sec)) return '';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function downsample(data, maxPoints) {
  if (data.length <= maxPoints) return data;
  const step = data.length / maxPoints;
  const result = [];
  for (let i = 0; i < maxPoints; i++) {
    const idx = Math.min(Math.floor(i * step), data.length - 1);
    result.push(data[idx]);
  }
  if (result[result.length - 1] !== data[data.length - 1]) {
    result.push(data[data.length - 1]);
  }
  return result;
}

function smoothArray(arr, windowSize = 5) {
  if (arr.length < windowSize) return arr;
  const result = [];
  const half = Math.floor(windowSize / 2);
  for (let i = 0; i < arr.length; i++) {
    let sum = 0;
    let count = 0;
    for (let j = Math.max(0, i - half); j <= Math.min(arr.length - 1, i + half); j++) {
      if (arr[j] != null && !isNaN(arr[j])) {
        sum += arr[j];
        count++;
      }
    }
    result.push(count > 0 ? sum / count : null);
  }
  return result;
}

function computeLapAverages(streams, laps, activity) {
  if (!laps || laps.length === 0) return null;
  const distStream = streams.distance?.data;
  const timeStream = streams.time?.data;
  if (!distStream || distStream.length === 0) return null;

  const totalDist = distStream[distStream.length - 1];
  const totalMovingTime = activity?.moving_time ? parseFloat(activity.moving_time) : (timeStream?.[timeStream.length - 1] ?? null);

  let cumDist = 0;
  let cumTime = 0;
  const lapAvgs = laps.map((lap, idx) => {
    let startKm;
    let endKm;
    let startSec;
    let endSec;
    let startIdx;
    let endIdx;

    if (lap.start_index != null && lap.end_index != null) {
      startIdx = lap.start_index;
      endIdx = Math.min(lap.end_index, distStream.length - 1);
      startKm = distStream[Math.min(startIdx, distStream.length - 1)] / 1000;
      endKm = distStream[endIdx] / 1000;
      if (timeStream) {
        startSec = parseFloat(timeStream[startIdx]);
        endSec = parseFloat(timeStream[endIdx]);
      } else if (totalDist > 0 && totalMovingTime) {
        startSec = (distStream[startIdx] / totalDist) * totalMovingTime;
        endSec = (distStream[endIdx] / totalDist) * totalMovingTime;
      } else {
        startSec = null;
        endSec = null;
      }
    } else {
      startKm = cumDist / 1000;
      cumDist += parseFloat(lap.distance) || 0;
      endKm = cumDist / 1000;
      const movTime = parseFloat(lap.moving_time) || 0;
      startSec = cumTime;
      cumTime += movTime;
      endSec = cumTime;
      startIdx = 0;
      endIdx = distStream.length - 1;
    }

    let altitude = null;
    let pace = null;
    let heartrate = null;
    let cadence = null;

    if (lap.average_speed && parseFloat(lap.average_speed) > 0.3) {
      pace = 1000 / parseFloat(lap.average_speed);
    }
    if (lap.average_heartrate != null) heartrate = parseFloat(lap.average_heartrate);
    if (lap.average_cadence != null) cadence = parseFloat(lap.average_cadence) * 2;

    const altData = streams.altitude?.data;
    if (altData && endIdx >= startIdx) {
      let sum = 0;
      let count = 0;
      for (let i = startIdx; i <= endIdx; i++) {
        if (altData[i] != null) {
          sum += altData[i];
          count++;
        }
      }
      if (count > 0) altitude = sum / count;
    }

    if (pace == null && streams.velocity_smooth?.data) {
      let sum = 0;
      let count = 0;
      for (let i = startIdx; i <= endIdx; i++) {
        const v = streams.velocity_smooth.data[i];
        if (v != null && v > 0.3) {
          sum += 1000 / v;
          count++;
        }
      }
      if (count > 0) pace = sum / count;
    }

    if (heartrate == null && streams.heartrate?.data) {
      let sum = 0;
      let count = 0;
      for (let i = startIdx; i <= endIdx; i++) {
        const v = streams.heartrate.data[i];
        if (v != null && v >= 40) {
          sum += v;
          count++;
        }
      }
      if (count > 0) heartrate = sum / count;
    }

    if (cadence == null && streams.cadence?.data) {
      let sum = 0;
      let count = 0;
      for (let i = startIdx; i <= endIdx; i++) {
        const v = streams.cadence.data[i];
        if (v != null && v > 0) {
          sum += v * 2;
          count++;
        }
      }
      if (count > 0) cadence = sum / count;
    }

    return { startKm, endKm, startSec, endSec, altitude, pace, heartrate, cadence };
  });

  return lapAvgs;
}


function buildChartData(streams, laps, activity) {
  const distStream = streams.distance?.data;
  const timeStream = streams.time?.data;
  if (!distStream || distStream.length === 0) {
    return { data: [], useDurationAxis: false };
  }

  const len = distStream.length;
  const totalDist = distStream[len - 1];
  const totalMovingTime = activity?.moving_time ? parseFloat(activity.moving_time) : null;
  const hasTime = timeStream && timeStream.length >= len;
  const hasDurationFallback = totalDist > 0 && totalMovingTime;

  const raw = [];

  const paceRaw = streams.velocity_smooth?.data;
  const paceSmoothed = paceRaw ? smoothArray(
    paceRaw.map(v => (v && v > 0.3) ? 1000 / v : null),
    7
  ) : null;

  for (let i = 0; i < len; i++) {
    const distM = distStream[i];
    const distKm = distM / 1000;
    let durationSec;
    if (hasTime && timeStream[i] != null) {
      durationSec = parseFloat(timeStream[i]);
    } else if (hasDurationFallback) {
      durationSec = (distM / totalDist) * totalMovingTime;
    } else {
      durationSec = null;
    }

    const point = { distanceM: distM, distanceKm: distKm, durationSec };

    if (streams.altitude?.data?.[i] != null) point.altitude = streams.altitude.data[i];
    if (paceSmoothed?.[i] != null) point.pace = paceSmoothed[i];

    const hr = streams.heartrate?.data?.[i];
    if (hr != null && hr >= 40) point.heartrate = hr;

    const cad = streams.cadence?.data?.[i];
    if (cad != null && cad > 0) point.cadence = cad * 2;

    const w = streams.watts?.data?.[i];
    if (w != null && w > 0) point.watts = w;

    if (streams.grade_smooth?.data?.[i] != null) point.grade = streams.grade_smooth.data[i];
    if (streams.temp?.data?.[i] != null) point.temp = streams.temp.data[i];

    raw.push(point);
  }

  let data = downsample(raw, 1500);

  const lapAvgs = computeLapAverages(streams, laps, activity);
  if (lapAvgs && lapAvgs.length > 0) {
    data = data.map((p) => {
      const lap = lapAvgs.find((l) =>
        (p.durationSec != null && l.endSec != null) ? (p.durationSec >= l.startSec && p.durationSec <= l.endSec) : (p.distanceKm >= l.startKm && p.distanceKm <= l.endKm)
      );
      const out = { ...p };
      if (lap) {
        if (lap.altitude != null) out.lapAvg_altitude = lap.altitude;
        if (lap.pace != null) out.lapAvg_pace = lap.pace;
        if (lap.heartrate != null) out.lapAvg_heartrate = lap.heartrate;
        if (lap.cadence != null) out.lapAvg_cadence = lap.cadence;
      }
      return out;
    });
  }

  const useDurationAxis = hasTime || hasDurationFallback;
  if (!useDurationAxis) {
    data = data.map((p) => ({ ...p, durationSec: p.distanceKm }));
  }
  return { data, useDurationAxis };
}

function computeStats(data, key) {
  const values = data.map(d => d[key]).filter(v => v != null && !isNaN(v));
  if (values.length === 0) return null;
  const sum = values.reduce((a, b) => a + b, 0);
  return {
    avg: sum / values.length,
    min: Math.min(...values),
    max: Math.max(...values),
  };
}

const TRIM_OPTS = {
  altitude:  { lo: 0, hi: 100 },
  pace:      { lo: 5, hi: 95, minFloor: 180, maxCeiling: 720 },
  heartrate: { lo: 2, hi: 98, minFloor: 50 },
  cadence:   { lo: 5, hi: 95, minFloor: 100, maxCeiling: 220 },
  watts:     { lo: 2, hi: 98, minFloor: 30 },
  grade:     { lo: 3, hi: 97 },
  temp:      { lo: 0, hi: 100 },
};

function computeTrimmedDomain(data, key) {
  const opts = TRIM_OPTS[key] || { lo: 2, hi: 98 };
  let values = data.map(d => d[key]).filter(v => v != null && !isNaN(v));
  if (opts.minFloor != null) values = values.filter(v => v >= opts.minFloor);
  if (opts.maxCeiling != null) values = values.filter(v => v <= opts.maxCeiling);
  if (values.length < 3) return null;

  values.sort((a, b) => a - b);
  const loIdx = Math.floor(values.length * opts.lo / 100);
  const hiIdx = Math.min(Math.ceil(values.length * opts.hi / 100) - 1, values.length - 1);
  const lo = values[loIdx];
  const hi = values[hiIdx];
  const pad = (hi - lo) * 0.06 || 1;

  return [lo - pad, hi + pad];
}

function formatVal(val, config) {
  if (val == null) return '-';
  return config.formatValue
    ? config.formatValue(val)
    : val.toFixed(config.decimals ?? 0);
}

const LAP_BG_KEYS = {
  altitude: 'lapAvg_altitude',
  pace: 'lapAvg_pace',
  heartrate: 'lapAvg_heartrate',
  cadence: 'lapAvg_cadence',
};

function ChartPanel({
  data,
  streamKey,
  config,
  brushIndex,
  onBrushChange,
  syncId,
  showBrush,
  activePoint,
  onHover,
  onLeave,
  lapBoundarySecs,
  plannedBoundarySecs,
  useDurationAxis,
}) {
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const gridOpacity = isDark ? 0.65 : 0.5;
  const splitOpacity = isDark ? 0.8 : 0.65;

  const stats = useMemo(() => computeStats(data, config.key), [data, config.key]);
  const trimmedDomain = useMemo(() => computeTrimmedDomain(data, config.key), [data, config.key]);
  if (!stats) return null;

  const Icon = config.icon;
  const yDomain = trimmedDomain || [Math.max(0, stats.min - 1), stats.max + 1];

  const lapBgKey = LAP_BG_KEYS[config.key];
  const hasLapBg = lapBgKey && data.some((d) => d[lapBgKey] != null);

  const liveVal = activePoint?.[config.key];
  const liveLapVal = activePoint?.[lapBgKey];
  const liveDuration = activePoint?.durationSec;
  const liveKm = activePoint?.distanceKm;
  const isHovering = activePoint != null && (liveVal != null || liveLapVal != null);

  const statDisplay = config.formatValue
    ? { avg: config.formatValue(stats.avg), best: config.formatValue(config.reversed ? stats.min : stats.max) }
    : { avg: stats.avg.toFixed(config.decimals ?? 0), best: (config.reversed ? stats.min : stats.max).toFixed(config.decimals ?? 0) };

  return (
    <div className='bg-card border border-border rounded-xl p-4'>
      <div className='flex items-center justify-between mb-2'>
        <div className='flex items-center gap-2'>
          <Icon className='h-4 w-4' style={{ color: config.color }} />
          <span className='text-sm font-medium text-foreground'>{config.label}</span>
        </div>
        <div className='text-xs text-muted-foreground'>
          {isHovering ? (
            <span className='transition-opacity'>
              <span className='font-medium' style={{ color: config.color }}>{formatVal(liveVal, config)}</span>
              {' '}{config.unit}
              {liveLapVal != null && (
                <>
                  <span className='mx-1.5 opacity-40'>·</span>
                  <span className='opacity-70'>lap avg</span>{' '}
                  <span className='font-medium'>{formatVal(liveLapVal, config)}</span>
                  {' '}{config.unit}
                </>
              )}
              <span className='mx-1.5 opacity-40'>·</span>
              {useDurationAxis && liveDuration != null ? formatDurationSec(liveDuration) : `${liveKm?.toFixed(2) ?? '—'} km`}
            </span>
          ) : (
            <span className='flex gap-3'>
              <span>avg <span className='font-medium text-foreground'>{statDisplay.avg}</span> {config.unit}</span>
              <span>{config.reversed ? 'best' : 'max'} <span className='font-medium text-foreground'>{statDisplay.best}</span> {config.unit}</span>
            </span>
          )}
        </div>
      </div>
      <ResponsiveContainer width='100%' height={180}>
        <ComposedChart
          data={data}
          syncId={syncId}
          margin={{ top: 5, right: 5, bottom: 0, left: -10 }}
          onMouseMove={(state) => {
            if (state?.activePayload?.[0]?.payload) {
              onHover(state.activePayload[0].payload);
            }
          }}
          onMouseLeave={onLeave}
        >
          <defs>
            {config.type === 'area' && (
              <linearGradient id={config.gradientId} x1='0' y1='0' x2='0' y2='1'>
                <stop offset='0%' stopColor={config.color} stopOpacity={0.4} />
                <stop offset='100%' stopColor={config.color} stopOpacity={0.05} />
              </linearGradient>
            )}
            {hasLapBg && (
              <linearGradient id={`lap-bg-${streamKey}`} x1='0' y1='0' x2='0' y2='1'>
                <stop offset='0%' stopColor={config.color} stopOpacity={0.12} />
                <stop offset='100%' stopColor={config.color} stopOpacity={0.03} />
              </linearGradient>
            )}
          </defs>
          <CartesianGrid strokeDasharray='3 3' stroke='hsl(var(--muted-foreground))' opacity={gridOpacity} />
          <XAxis
            dataKey='durationSec'
            type='number'
            domain={['dataMin', 'dataMax']}
            tickFormatter={v => useDurationAxis ? formatDurationSec(v) : `${parseFloat(v).toFixed(1)}`}
            tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={{ stroke: 'hsl(var(--border))' }}
            tickLine={false}
          />
          <YAxis
            domain={yDomain}
            allowDataOverflow
            reversed={!!config.reversed}
            tickFormatter={config.formatTick || (v => v.toFixed(config.decimals ?? 0))}
            tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={false}
            tickLine={false}
            width={45}
          />
          <Tooltip
            content={() => null}
            cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeWidth: 1, strokeDasharray: '4 4' }}
          />
          {config.key === 'grade' && (
            <ReferenceLine y={0} stroke='hsl(var(--muted-foreground))' strokeDasharray='3 3' opacity={0.5} />
          )}
          {lapBoundarySecs?.map((sec, i) => (
            <ReferenceLine
              key={`lap-${i}`}
              x={sec}
              stroke='hsl(var(--muted-foreground))'
              strokeDasharray='4 4'
              strokeOpacity={splitOpacity}
            />
          ))}
          {plannedBoundarySecs?.map((sec, i) => (
            <ReferenceLine
              key={`plan-${i}`}
              x={sec}
              stroke='hsl(var(--primary))'
              strokeDasharray='3 3'
              strokeOpacity={0.9}
            />
          ))}
          {hasLapBg && (
            <Area
              dataKey={lapBgKey}
              type='stepAfter'
              stroke={config.color}
              strokeOpacity={0.2}
              strokeWidth={1}
              fill={`url(#lap-bg-${streamKey})`}
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
          )}
          {config.type === 'area' ? (
            <Area
              dataKey={config.key}
              type='monotone'
              stroke={config.color}
              strokeWidth={1.5}
              fill={`url(#${config.gradientId})`}
              dot={false}
              activeDot={{ r: 4, fill: config.color, stroke: '#fff', strokeWidth: 2 }}
              isAnimationActive={false}
              connectNulls
            />
          ) : (
            <Line
              dataKey={config.key}
              type='monotone'
              stroke={config.color}
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 4, fill: config.color, stroke: '#fff', strokeWidth: 2 }}
              isAnimationActive={false}
              connectNulls
            />
          )}
          {showBrush && (
            <Brush
              dataKey='durationSec'
              height={30}
              stroke='hsl(var(--border))'
              fill='hsl(var(--muted))'
              tickFormatter={v => useDurationAxis ? formatDurationSec(parseFloat(v)) : `${parseFloat(v).toFixed(1)} km`}
              startIndex={brushIndex?.[0]}
              endIndex={brushIndex?.[1]}
              onChange={onBrushChange}
              travellerWidth={8}
            >
              <ComposedChart>
                <Area
                  dataKey={config.key}
                  type='monotone'
                  stroke={config.color}
                  fill={config.color}
                  fillOpacity={0.15}
                  strokeWidth={1}
                  dot={false}
                  isAnimationActive={false}
                />
              </ComposedChart>
            </Brush>
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

const StreamCharts = ({ streams, laps, activity, plannedBoundarySecs = [] }) => {
  const [brushIndex, setBrushIndex] = useState(null);
  const [activePoint, setActivePoint] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const collapsedRef = useRef({});
  const syncId = 'activityStreams';

  collapsedRef.current = collapsed;

  const { data: chartData, useDurationAxis } = useMemo(() => buildChartData(streams, laps, activity), [streams, laps, activity]);

  const availableStreams = useMemo(
    () => STREAM_ORDER.filter(k => {
      const cfg = CHART_CONFIG[k];
      return cfg && chartData.some(d => d[cfg.key] != null);
    }),
    [chartData]
  );

  useEffect(() => {
    if (availableStreams.length === 0) return;
    let cancelled = false;
    api.get('/api/users/settings')
      .then((res) => {
        if (cancelled) return;
        const p = res.data?.streamChartsPreferences || res.data?.settings?.stream_charts_preferences || {};
        const saved = p.collapsed || {};
        const merged = {};
        availableStreams.forEach(k => {
          if (saved[k] === true) merged[k] = true;
        });
        setCollapsed(prev => {
          if (Object.keys(prev).length > 0) return prev;
          return Object.keys(merged).length > 0 ? merged : prev;
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [availableStreams.join(',')]);

  const saveCollapsed = useCallback((next) => {
    api.put('/api/users/settings', { streamChartsPreferences: { collapsed: next } }).catch(() => {});
  }, []);

  const lapBoundarySecs = useMemo(() => {
    if (!useDurationAxis) return [];
    const lapAvgs = computeLapAverages(streams, laps, activity);
    if (!lapAvgs || lapAvgs.length <= 1) return [];
    return lapAvgs.slice(0, -1).map((l) => l.endSec).filter((s) => s != null);
  }, [streams, laps, activity, useDurationAxis]);

  const handleBrushChange = useCallback((range) => {
    if (range) setBrushIndex([range.startIndex, range.endIndex]);
  }, []);

  const handleHover = useCallback((point) => setActivePoint(point), []);
  const handleLeave = useCallback(() => setActivePoint(null), []);

  const toggleChart = useCallback((key) => {
    setCollapsed(prev => {
      const next = { ...prev, [key]: !prev[key] };
      saveCollapsed(next);
      return next;
    });
  }, [saveCollapsed]);

  const allCollapsed = availableStreams.length > 0 && availableStreams.every(k => collapsed[k]);
  const allExpanded = availableStreams.every(k => !collapsed[k]);

  const toggleAll = useCallback(() => {
    if (allExpanded) {
      const next = {};
      availableStreams.forEach(k => { next[k] = true; });
      setCollapsed(next);
      saveCollapsed(next);
    } else {
      setCollapsed({});
      saveCollapsed({});
    }
  }, [allExpanded, availableStreams, saveCollapsed]);

  if (chartData.length === 0 || availableStreams.length === 0) {
    return <p className='text-sm text-muted-foreground'>No stream data available.</p>;
  }

  const totalKm = chartData[chartData.length - 1]?.distanceKm ?? 0;
  const totalDurationSec = chartData[chartData.length - 1]?.durationSec ?? 0;
  const totalPoints = streams.distance?.originalSize ?? chartData.length;

  const visibleStreams = availableStreams.filter(k => !collapsed[k]);
  const firstVisibleIdx = visibleStreams.length > 0 ? visibleStreams[0] : null;

  return (
    <div className='space-y-3'>
      {/* Toolbar */}
      <div className='flex items-center justify-between flex-wrap gap-2'>
        <div className='flex items-center gap-2 flex-wrap'>
          <button
            onClick={toggleAll}
            className='flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border border-border bg-muted/50 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors'
            title={allExpanded ? 'Collapse all' : 'Expand all'}
          >
            <ChevronsUpDown className='h-3.5 w-3.5' />
          </button>
          {availableStreams.map(streamKey => {
            const cfg = CHART_CONFIG[streamKey];
            const Icon = cfg.icon;
            const isVisible = !collapsed[streamKey];
            return (
              <button
                key={streamKey}
                onClick={() => toggleChart(streamKey)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all border ${
                  isVisible
                    ? 'border-transparent text-white'
                    : 'border-border bg-muted/30 text-muted-foreground hover:bg-muted/60'
                }`}
                style={isVisible ? { backgroundColor: cfg.color } : undefined}
              >
                <Icon className='h-3 w-3' />
                {cfg.label}
              </button>
            );
          })}
        </div>
        <div className='flex items-center gap-3'>
          {brushIndex && (
            <button
              onClick={() => setBrushIndex(null)}
              className='text-xs text-primary hover:underline'
            >
              Reset zoom
            </button>
          )}
          <p className='text-xs text-muted-foreground'>
            {totalPoints.toLocaleString()} pts
            {useDurationAxis && ` · ${formatDurationSec(totalDurationSec)}`}
            {' · '}{totalKm.toFixed(2)} km
          </p>
        </div>
      </div>

      {/* Charts */}
      {availableStreams.map((streamKey) => {
        if (collapsed[streamKey]) return null;
        return (
          <ChartPanel
            key={streamKey}
            data={chartData}
            streamKey={streamKey}
            config={CHART_CONFIG[streamKey]}
            brushIndex={brushIndex}
            onBrushChange={handleBrushChange}
            syncId={syncId}
            showBrush={streamKey === firstVisibleIdx}
            activePoint={activePoint}
            onHover={handleHover}
            onLeave={handleLeave}
            lapBoundarySecs={lapBoundarySecs}
            plannedBoundarySecs={plannedBoundarySecs}
            useDurationAxis={useDurationAxis}
          />
        );
      })}

      {allCollapsed && (
        <div className='text-center py-8 text-sm text-muted-foreground'>
          All charts are hidden. Click a metric above to show it.
        </div>
      )}
    </div>
  );
};

export default StreamCharts;
